import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react"
import { View, Text } from "./primitives"

type PermissionStatus = "granted" | "denied" | "not-determined" | "restricted"

async function queryMediaPermission(kind: "camera" | "microphone"): Promise<PermissionStatus> {
  if (typeof navigator === "undefined") return "denied"
  try {
    if (navigator.permissions?.query) {
      const status = await navigator.permissions.query({ name: kind as PermissionName })
      if (status.state === "granted") return "granted"
      if (status.state === "denied") return "denied"
    }
  } catch {
    // permissions.query may fail for microphone on some browsers
  }
  return "not-determined"
}

async function requestMediaPermission(constraints: MediaStreamConstraints): Promise<PermissionStatus> {
  if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) return "denied"
  try {
    const stream = await navigator.mediaDevices.getUserMedia(constraints)
    stream.getTracks().forEach((track) => track.stop())
    return "granted"
  } catch {
    return "denied"
  }
}

type CodeScannerConfig = {
  codeTypes?: string[]
  onCodeScanned?: (codes: Array<{ value?: string | null }>) => void
}

const MOCK_DEVICE = {
  id: "web-camera",
  position: "back" as const,
  hasFlash: false,
  hasTorch: false,
  isMultiCam: false,
  supportsFocus: false,
  minFocusDistance: 0,
  neutralZoom: 1,
  maxZoom: 1,
  formats: [],
}

export type CameraRef = {
  takePhoto?: (opts?: unknown) => Promise<{ path: string }>
  startRecording?: (opts: unknown) => Promise<void>
  stopRecording?: () => Promise<void>
}

const CameraComponent = forwardRef<
  CameraRef,
  {
    device?: unknown
    isActive?: boolean
    photo?: boolean
    video?: boolean
    audio?: boolean
    preview?: boolean
    enableZoomGesture?: boolean
    photoQualityBalance?: string
    codeScanner?: CodeScannerConfig
    style?: unknown
  }
>(function Camera(_props, ref) {
  useImperativeHandle(ref, () => ({
    takePhoto: async () => ({ path: "" }),
    startRecording: async () => {},
    stopRecording: async () => {},
  }))

  const isActive = _props.isActive !== false
  const codeScanner = _props.codeScanner

  const videoRef = useRef<HTMLVideoElement | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const rafRef = useRef<number | null>(null)
  const [status, setStatus] = useState<"idle" | "starting" | "ready" | "no-permission" | "no-support">("idle")

  const detector = useMemo(() => {
    const Any = globalThis as any
    if (typeof Any.BarcodeDetector !== "function") return null
    try {
      const formats = codeScanner?.codeTypes?.includes("qr") ? ["qr_code"] : undefined
      return new Any.BarcodeDetector(formats ? { formats } : undefined)
    } catch {
      return null
    }
  }, [codeScanner?.codeTypes])

  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      setStatus("no-support")
      return
    }
    if (!isActive) return

    let cancelled = false
    const start = async () => {
      setStatus("starting")
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" } },
          audio: false,
        })
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop())
          return
        }
        streamRef.current = stream
        const video = videoRef.current
        if (video) {
          video.srcObject = stream
          // iOS Safari/WKWebView requires these for autoplay
          video.muted = true
          video.playsInline = true
          await video.play().catch(() => {
            // ignore; user gesture might be required, we'll still show the view
          })
        }
        setStatus("ready")
      } catch {
        setStatus("no-permission")
      }
    }

    void start()

    return () => {
      cancelled = true
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop())
        streamRef.current = null
      }
    }
  }, [isActive])

  useEffect(() => {
    if (!isActive) return
    if (!detector) return
    if (!codeScanner?.onCodeScanned) return

    let mounted = true
    const tick = async () => {
      const video = videoRef.current
      if (!mounted || !video) return
      if (video.readyState >= 2) {
        try {
          const results = (await detector.detect(video)) as Array<{ rawValue?: string }>
          if (results?.length) {
            const raw = results[0]?.rawValue
            if (raw) {
              codeScanner.onCodeScanned([{ value: raw }])
            }
          }
        } catch {
          // ignore detection errors
        }
      }
      rafRef.current = requestAnimationFrame(() => void tick())
    }

    rafRef.current = requestAnimationFrame(() => void tick())
    return () => {
      mounted = false
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
    }
  }, [detector, isActive, codeScanner])

  return (
    <View
      style={{
        flex: 1,
        alignItems: "center",
        justifyContent: "center",
        background: "#111",
        minHeight: 200,
      }}
    >
      {status === "no-support" ? (
        <Text style={{ color: "#fff", fontSize: 14 }}>当前环境不支持摄像头</Text>
      ) : status === "no-permission" ? (
        <Text style={{ color: "#fff", fontSize: 14 }}>等待摄像头权限授权...</Text>
      ) : detector == null ? (
        <Text style={{ color: "#fff", fontSize: 14 }}>当前浏览器不支持扫码能力</Text>
      ) : (
        <video
          ref={(el) => {
            videoRef.current = el
          }}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
          playsInline
          muted
        />
      )}
    </View>
  )
})

export const Camera = Object.assign(CameraComponent, {
  getCameraPermissionStatus: () => queryMediaPermission("camera"),
  getMicrophonePermissionStatus: () => queryMediaPermission("microphone"),
  requestCameraPermission: () => requestMediaPermission({ video: true }),
  requestMicrophonePermission: () => requestMediaPermission({ audio: true }),
})

export const useCameraDevice = (_position?: string) => MOCK_DEVICE

export const useCodeScanner = (opts?: CodeScannerConfig) => opts
